import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./ADS.scss";

const ADS_IMG = (file) => imageUrl("ads", file);
const VIEW_ARROW = imageUrl("dfs", "ViewArrow.svg");
const EMBED_URL = "https://ads-agent-analytics.lovable.app";

const challengeItems = [
  {
    title:
      "Manual test triggering and validation leading to fatigue and slower turnaround times",
    image: "challenge-manual-testing.svg",
  },
  {
    title:
      "Inconsistent screenshot and evidence capture affecting reporting quality",
    image: "challenge-evidence.svg",
  },
  {
    title:
      "Repeated failure re-runs and triage delays impacting sprint timelines",
    image: "challenge-reruns.svg",
  },
  {
    title:
      "Fragmented execution data limiting visibility into recurring failure patterns",
    image: "challenge-fragmented-data.svg",
  },
  {
    title:
      "Scaling QA quality with a small manual team becoming increasingly difficult",
    image: "challenge-scaling.svg",
  },
];

const collageImages = [
  "solution-collage-1.svg",
  "solution-collage-2.svg",
  "solution-collage-3.svg",
  "solution-collage-4.svg",
];

const featureItems = [
  {
    title: "Automated Test Execution",
    text: "Executes scheduled and on-demand test cases autonomously.",
    icon: "feature-automation.svg",
  },
  {
    title: "Smart Result Logging",
    text:
      "Automatically updates Azure DevOps with pass or fail status and generates evidence for each run.",
    icon: "feature-logging.svg",
  },
  {
    title: "Detailed Agent Logging",
    text:
      "Attaches step-by-step execution logs for every test case with timestamped evidence.",
    icon: "feature-detail.svg",
  },
  {
    title: "Historical Context Retention",
    text:
      "Maintains consistency across runs by reusing validated execution flows and prior decisions.",
    icon: "feature-history.svg",
  },
  {
    title: "Flexible Execution via API",
    text:
      "Supports full, selective, or missed test execution through API-triggered workflows.",
    icon: "feature-api.svg",
  },
];

const stackItems = [
  {
    name: "Azure DevOps",
    file: "logo-azure-devops.svg",
    className: "ads-stack__logo--azure",
  },
  {
    name: "Selenium",
    file: "logo-selenium.svg",
    className: "ads-stack__logo--selenium",
  },
  {
    name: "JSON",
    file: "logo-json.svg",
    className: "ads-stack__logo--json",
  },
  {
    name: "Python",
    file: "logo-python.svg",
    className: "ads-stack__logo--python",
  },
];

const impactItems = [
  "Over 60% reduction in manual validation time for nightly test runs.",
  "Eliminated missed screenshots and incorrect status logging.",
  "QA results available within minutes of execution completion.",
  "Each failure includes timestamped logs and screenshot evidence.",
  "Every test follows an identical validation flow, reducing human variation.",
  "Manual testers can now prioritise exploratory testing and new feature validation.",
  "Structured reports are available before deployment decisions.",
];

const ADS = () => {
  return (
    <>
      <MegaHeader />

      <section className="ads-hero">
        <div
          className="ads-hero__background"
          aria-hidden="true"
          style={{ backgroundImage: `url(${ADS_IMG("hero.svg")})` }}
        />
        <div className="ads-hero__content">
          <h1 className="ads-hero__title">AI-Powered QA Automation Agent</h1>
          <p className="ads-hero__description">
            An intelligent QA agent that autonomously executes test cases, 
            captures failure evidence, and logs results directly in Azure DevOps, 
            reducing manual effort and accelerating release cycles. 
          </p>
        </div>
      </section>

      <section className="ads-section ads-challenge">
        <div className="ads-container">
          <h2 className="ads-section__title">The Challenge</h2>
          <p className="ads-section__intro">
            Our client operates a large-scale analytics and reporting platform
            with continuous updates and nearly 7,000 test cases. Although an
            automated suite ran nightly, significant manual intervention was
            still required, particularly for regression and new feature testing.
          </p>

          <div className="ads-challenge__grid">
            {challengeItems.map((item) => (
              <article key={item.title} className="ads-challenge-card">
                <img
                  src={ADS_IMG(item.image)}
                  alt=""
                  aria-hidden="true"
                  className="ads-challenge-card__icon"
                />
                <p className="ads-challenge-card__text">{item.title}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ads-section ads-solution">
        <div className="ads-container ads-solution__layout">
          <div className="ads-solution__content">
            <h2 className="ads-section__title">Solution</h2>
            <p className="ads-section__intro">
            The  QA Agent was developed as an intelligent automation bot 
            integrated with Azure DevOps to execute, monitor, and document 
            test cases autonomously. 
            </p>
            <p className="ads-section__intro">
           Initially deployed for the Location Dashboard module, the agent runs 
           scheduled nightly test executions and supports on-demand execution as 
           needed. It marks each test case as Pass or Fail directly within Azure 
           DevOps, automatically captures screenshots at the point of failure, 
           and generates AI-based failure summaries for quick understanding. 
           In addition, it logs detailed execution steps to ensure full traceability 
           and easier debugging.
            </p>
            <p className="ads-section__intro ads-solution__intro-last">
              By embedding automation into the CI/CD pipeline, the QA Agent 
              ensures continuous validation with minimal human intervention 
              while maintaining estimator-level oversight where required. 
            </p>
          </div>

          <div className="ads-solution__collage" aria-label="ADS solution visuals">
            {collageImages.map((file) => (
              <img
                key={file}
                src={ADS_IMG(file)}
                alt=""
                aria-hidden="true"
                className="ads-solution__image"
              />
            ))}
          </div>
        </div>
      </section>

      <section className="ads-section ads-features">
        <div className="ads-container">
          <h2 className="ads-section__title">Key Features</h2>

          <div className="ads-features__grid">
            {featureItems.map((item) => (
              <article key={item.title} className="ads-feature-card">
                <img
                  src={ADS_IMG(item.icon)}
                  alt=""
                  aria-hidden="true"
                  className="ads-feature-card__icon"
                />
                <div className="ads-feature-card__content">
                  <h3 className="ads-feature-card__title">{item.title}</h3>
                  <p className="ads-feature-card__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ads-section ads-dashboard">
        <div className="ads-container">
          <div className="ads-dashboard__link-row">
            <a
              href={EMBED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ads-dashboard__link"
              aria-label="View ADS demo in a new tab"
            >
              <span>View Demo</span>
              <img
                src={VIEW_ARROW}
                alt=""
                aria-hidden="true"
                className="ads-dashboard__link-icon"
              />
            </a>
          </div>

          <div className="ads-dashboard__frame">
            <iframe
              src={EMBED_URL}
              title="ADS QA Automation Agent Demo"
              className="ads-dashboard__iframe"
              allowFullScreen
              allow="fullscreen"
              scrolling="yes"
            />
          </div>

          <div className="ads-stack">
            {stackItems.map((item) => (
              <img
                key={item.name}
                src={ADS_IMG(item.file)}
                alt={item.name}
                className={`ads-stack__logo ${item.className}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className="ads-impact"
        style={{ backgroundImage: `url(${ADS_IMG("footer.svg")})` }}
      >
        <div className="ads-impact__overlay" aria-hidden="true" />
        <div className="ads-container ads-impact__content">
          <h2 className="ads-section__title ads-impact__title">Impact</h2>
          <ul className="ads-impact__list">
            {impactItems.map((item) => (
              <li key={item}>
                <img
                  src={ADS_IMG("checkmark.svg")}
                  alt=""
                  aria-hidden="true"
                  className="ads-impact__check"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default ADS;
